import React, { useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { RotateCcw } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { money } from "@/lib/api";
import { consultantBalances } from "@/lib/payouts";
import Stat from "@/components/Stat";

const RANGES = [
  { id: "month", label: "This month" },
  { id: "3m", label: "3 months" },
  { id: "6m", label: "6 months" },
  { id: "year", label: "This year" },
  { id: "all", label: "All time" },
];

const METRICS = [
  { id: "visits", label: "Visits" },
  { id: "clinic_share", label: "Clinic Share" },
  { id: "cons_fee", label: "Consultant Fees" },
  { id: "lab_cost", label: "Lab Costs" },
];

// Local-calendar month key. (toISOString() would shift to UTC and, in IST, roll the
// 1st of the month back to the last day of the previous month.)
const monthKey = (y, m) => {
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const startOf = (range) => {
  const d = new Date();
  const back = { month: 0, "3m": 2, "6m": 5 }[range];
  if (back !== undefined) return `${monthKey(d.getFullYear(), d.getMonth() - back)}-01`;
  if (range === "year") return `${d.getFullYear()}-01-01`;
  return "";
};

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (key) => `${MONTH_LABELS[Number(key.slice(5, 7)) - 1]} '${key.slice(2, 4)}`;

// Every month from `from` to `to` inclusive (YYYY-MM keys), so quiet months show as zero bars.
const monthSpan = (from, to) => {
  const out = [];
  let y = Number(from.slice(0, 4)), m = Number(from.slice(5, 7)) - 1;
  for (let key = monthKey(y, m); key <= to && out.length < 600; key = monthKey(y, ++m)) out.push(key);
  return out;
};

export default function Overview() {
  const { data, showMoney, accent } = useApp();
  const [range, setRange] = useState("all");
  const [year, setYear] = useState("");
  const [treatment, setTreatment] = useState("");
  const [consultant, setConsultant] = useState("");
  const [metric, setMetric] = useState("visits");

  const treatments = useMemo(() => [...new Set(data.visits.map((v) => v.treatment).filter(Boolean))].sort(), [data.visits]);

  const scoped = useMemo(() => data.visits.filter((v) => {
    if (treatment && v.treatment !== treatment) return false;
    if (consultant && v.consultant_id !== consultant) return false;
    return true;
  }), [data.visits, treatment, consultant]);

  const inPeriod = useMemo(() => {
    const from = startOf(range);
    return (d) => (year ? (d || "").slice(0, 4) === year : !(from && (d || "") < from));
  }, [range, year]);
  const visits = useMemo(() => scoped.filter((v) => inPeriod(v.date)), [scoped, inPeriod]);

  // What the clinic still owes consultants: Consultant Fees on their visits less payouts recorded.
  const payables = useMemo(
    () => consultantBalances(data.consultants.filter((c) => !consultant || c.id === consultant), data.visits, data.settlements)
      .reduce((a, r) => a + Math.max(0, r.outstanding), 0),
    [data.consultants, data.visits, data.settlements, consultant]
  );

  const kpis = useMemo(() => ({
    patients: new Set(visits.map((v) => v.patient_id)).size,
    visits: visits.length,
    clinic: visits.reduce((a, v) => a + Number(v.clinic_share || 0), 0),
    fees: visits.reduce((a, v) => a + Number(v.consultant_fee || 0), 0),
    lab: visits.reduce((a, v) => a + Number(v.lab_cost || 0), 0),
  }), [visits]);

  const monthly = useMemo(() => {
    const blank = () => ({ visits: 0, clinic_share: 0, cons_fee: 0, lab_cost: 0 });
    const map = {};
    const keys = visits.map((v) => (v.date || "").slice(0, 7)).filter((k) => k.length === 7).sort();
    const now = new Date();
    const thisMonth = monthKey(now.getFullYear(), now.getMonth());
    let span = [];
    if (year) span = monthSpan(`${year}-01`, `${year}-12`);
    else if (keys.length) {
      const from = startOf(range).slice(0, 7) || keys[0];
      const last = keys[keys.length - 1];
      span = monthSpan(from, last > thisMonth ? last : thisMonth);
    }
    span.forEach((k) => { map[k] = { month: year ? MONTH_LABELS[Number(k.slice(5, 7)) - 1] : monthLabel(k), key: k, ...blank() }; });
    visits.forEach((v) => {
      const m = (v.date || "").slice(0, 7);
      if (m.length !== 7) return;
      map[m] = map[m] || { month: monthLabel(m), key: m, ...blank() };
      map[m].visits += 1;
      map[m].clinic_share += Number(v.clinic_share || 0);
      map[m].cons_fee += Number(v.consultant_fee || 0);
      map[m].lab_cost += Number(v.lab_cost || 0);
    });
    return Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
  }, [visits, year, range]);

  const yearly = useMemo(() => {
    const map = {};
    scoped.forEach((v) => {
      const y = (v.date || "").slice(0, 4);
      if (y.length !== 4) return;
      map[y] = map[y] || { year: y, visits: 0, clinic_share: 0, cons_fee: 0, lab_cost: 0, patients: new Set() };
      map[y].visits += 1;
      map[y].clinic_share += Number(v.clinic_share || 0);
      map[y].cons_fee += Number(v.consultant_fee || 0);
      map[y].lab_cost += Number(v.lab_cost || 0);
      map[y].patients.add(v.patient_id);
    });
    return Object.values(map)
      .map((r) => ({ ...r, patients: r.patients.size }))
      .sort((a, b) => a.year.localeCompare(b.year));
  }, [scoped]);

  // Visits without a treatment or consultant are counted in a footnote, not ranked as a "—" row.
  const topTreatments = useMemo(() => {
    const map = {};
    let none = 0;
    visits.forEach((v) => { const t = (v.treatment || "").trim(); if (!t) none += 1; else map[t] = (map[t] || 0) + 1; });
    const rows = Object.entries(map).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 8);
    return { rows, none };
  }, [visits]);

  const consultantRank = useMemo(() => {
    const map = {};
    let none = 0;
    visits.forEach((v) => {
      const name = data.consultants.find((c) => c.id === v.consultant_id)?.name;
      if (!name) { none += 1; return; }
      map[name] = map[name] || { name, visits: 0, fees: 0 };
      map[name].visits += 1;
      map[name].fees += Number(v.consultant_fee || 0);
    });
    return { rows: Object.values(map).sort((a, b) => b.visits - a.visits).slice(0, 8), none };
  }, [visits, data.consultants]);

  const hide = (v) => (showMoney ? money(v) : "•••");
  const clearFilters = () => { setRange("all"); setYear(""); setTreatment(""); setConsultant(""); };
  const metricLabel = METRICS.find((m) => m.id === metric).label;
  const chartTotal = monthly.reduce((a, m) => a + m[metric], 0);

  return (
    <div className="space-y-4" data-testid="overview-page">
      <div>
        <h1 className="dm-page-title">
          Clinic <span className="font-extrabold">overview</span>
        </h1>
      </div>

      <div className="stagger grid grid-cols-2 gap-3 lg:grid-cols-3" data-testid="ov-stats">
        <Stat hero label="Clinic Share" value={hide(kpis.clinic)} testid="ov-kpi-clinic-revenue" className="col-span-2 lg:col-span-1" />
        <Stat label="Consultant Fees" value={hide(kpis.fees)} testid="ov-kpi-consultant-fees" />
        <Stat label="Lab Costs" value={hide(kpis.lab)} testid="ov-kpi-lab-costs" />
        <Stat label="Owed to consultants" value={hide(payables)} tone="warn" to="/app/consultants" testid="ov-kpi-consultant-payables" />
        <Stat label="Patients" value={kpis.patients.toLocaleString("en-IN")} testid="ov-kpi-patients" />
        <Stat label="Visits" value={kpis.visits.toLocaleString("en-IN")} testid="ov-kpi-visits" />
      </div>

      <section className="flex flex-wrap items-center gap-2.5" data-testid="ov-filters">
        <div className="dm-seg" data-testid="ov-range" role="group" aria-label="Period">
          {RANGES.map((r) => (
            <button key={r.id} data-testid={`ov-range-${r.id}`} onClick={() => { setRange(r.id); setYear(""); }} aria-pressed={!year && range === r.id}
              className={`dm-seg-btn ${!year && range === r.id ? "dm-seg-on" : ""}`}>{r.label}</button>
          ))}
          {year && (
            <button data-testid="ov-year-active" onClick={() => setYear("")} className="dm-seg-btn dm-seg-on" aria-label={`Showing ${year}. Clear year`}>
              {year} <RotateCcw size={11} />
            </button>
          )}
        </div>
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
          <select data-testid="ov-treatment" aria-label="Treatment" className="dm-input !rounded-full !py-1.5 text-[13px] sm:!w-auto sm:min-w-[170px]" value={treatment} onChange={(e) => setTreatment(e.target.value)}>
            <option value="">All treatments</option>
            {treatments.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="ov-consultant" aria-label="Consultant" className="dm-input !rounded-full !py-1.5 text-[13px] sm:!w-auto sm:min-w-[170px]" value={consultant} onChange={(e) => setConsultant(e.target.value)}>
            <option value="">All consultants</option>
            {data.consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {(range !== "all" || year || treatment || consultant) && (
          <button data-testid="ov-clear" onClick={clearFilters} className="dm-btn-quiet !py-1.5 text-sm"><RotateCcw size={13} /> Clear filters</button>
        )}
      </section>

      <section className="dm-panel p-4 md:p-5">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-head text-[15px] font-semibold text-slate-900" data-testid="ov-chart-title">
            {year ? `${year} — month by month` : "Monthly Activity"}
            {!year && monthly.length > 1 && (
              <span className="ml-2 text-sm font-normal text-slate-500" data-testid="ov-chart-span">
                {monthLabel(monthly[0].key)} – {monthLabel(monthly[monthly.length - 1].key)}
              </span>
            )}
          </h2>
          <div className="dm-seg" data-testid="ov-metrics" role="group" aria-label="Chart metric">
            {METRICS.map((m) => (
              <button key={m.id} data-testid={`ov-metric-${m.id}`} onClick={() => setMetric(m.id)} aria-pressed={metric === m.id}
                className={`dm-seg-btn ${metric === m.id ? "dm-seg-on" : ""}`}>{m.label}</button>
            ))}
          </div>
        </div>
        {monthly.length === 0 ? (
          <div className="dm-empty" data-testid="ov-chart-empty">No activity for this filter combination.</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false}
                  interval="preserveStartEnd" minTickGap={12} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, color: "#0f172a", boxShadow: "0 8px 24px rgba(15,23,42,.12)" }}
                  formatter={(v) => (metric === "visits" ? v : money(v))} />
                <Bar dataKey={metric} fill={accent} radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
            <p className="mt-3 text-sm text-slate-600" data-testid="chart-totals">
              {metricLabel}: <span className="font-mono font-semibold text-slate-800">{metric === "visits" ? chartTotal : showMoney ? money(chartTotal) : "•••"}</span>
            </p>
          </>
        )}
      </section>

      <section className="dm-panel" data-testid="ov-year-compare">
        <div className="dm-panel-head">
          <h2 className="font-head text-lg font-semibold text-slate-800">Year vs year</h2>
          <span className="text-xs text-slate-500">{metricLabel} · change on previous year</span>
        </div>
        {yearly.length === 0 ? (
          <div className="dm-empty m-4">No yearly data yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="dm-table w-full">
              <thead>
                <tr>
                  <th>Year</th><th className="text-right">Patients</th><th className="text-right">Visits</th>
                  <th className="text-right">Clinic Share</th><th className="text-right">Consultant Fee</th>
                  <th className="text-right">Lab Cost</th><th className="text-right">{metricLabel} vs prev</th>
                </tr>
              </thead>
              <tbody>
                {yearly.map((r, i) => {
                  const prev = i > 0 ? yearly[i - 1][metric] : null;
                  const cur = r[metric];
                  const pct = prev ? ((cur - prev) / prev) * 100 : null;
                  const m = (n) => (showMoney ? money(n) : "•••");
                  return (
                    <tr key={r.year} data-testid={`ov-year-row-${r.year}`}
                      className={year === r.year ? "bg-slate-100/70" : ""}>
                      <td className="font-mono font-semibold text-slate-800">
                        <button onClick={() => setYear(r.year)} data-testid={`ov-year-pick-${r.year}`} className="hover:underline">{r.year}</button>
                      </td>
                      <td className="text-right font-mono text-slate-700">{r.patients}</td>
                      <td className="text-right font-mono text-slate-700">{r.visits}</td>
                      <td className="text-right font-mono text-slate-700">{m(r.clinic_share)}</td>
                      <td className="text-right font-mono text-slate-700">{m(r.cons_fee)}</td>
                      <td className="text-right font-mono text-slate-700">{m(r.lab_cost)}</td>
                      <td className="text-right font-mono">
                        {pct === null ? <span className="text-slate-500">—</span> : (
                          <span className={pct >= 0 ? "text-emerald-600" : "text-red-600"}>
                            {pct >= 0 ? "▲" : "▼"} {Math.abs(pct).toFixed(0)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="grid gap-3 lg:grid-cols-2">
        <section className="dm-panel overflow-hidden">
          <div className="dm-panel-head">
            <h2 className="font-head text-lg font-semibold text-slate-800">Top Treatments</h2>
            <span className="text-xs text-slate-500">Ranked by visits</span>
          </div>
          {topTreatments.rows.length === 0 ? (
            <div className="dm-empty m-5">No treatments in this period.</div>
          ) : (
            <ul className="space-y-3.5 px-5 py-5" data-testid="ov-treatments">
              {topTreatments.rows.map((t) => (
                <li key={t.name}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="truncate text-slate-800">{t.name}</span>
                    <span className="dm-figure shrink-0 font-semibold text-slate-900">{t.count.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="mt-1.5 h-1 rounded-full dm-accent-bg" style={{ width: `${Math.max(4, (t.count / topTreatments.rows[0].count) * 100)}%`, opacity: 0.85 }} />
                </li>
              ))}
            </ul>
          )}
          {topTreatments.none > 0 && <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">{topTreatments.none.toLocaleString("en-IN")} visits have no treatment recorded.</p>}
        </section>

        <section className="dm-panel overflow-hidden">
          <div className="dm-panel-head">
            <h2 className="font-head text-lg font-semibold text-slate-800">Consultants</h2>
            <span className="text-xs text-slate-500">{showMoney ? "Visits and fees" : "Visits"}</span>
          </div>
          {consultantRank.rows.length === 0 ? (
            <div className="dm-empty m-5">No consultant visits in this period.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="ov-consultants">
              {consultantRank.rows.map((c) => (
                <li key={c.name} className="flex items-center justify-between gap-3 px-5 py-3">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-800">{c.name}</span>
                    <span className="block text-xs text-slate-500">{c.visits.toLocaleString("en-IN")} visit{c.visits === 1 ? "" : "s"}</span>
                  </span>
                  {showMoney && <span className="dm-figure shrink-0 text-sm font-semibold text-slate-900">{money(c.fees)}</span>}
                </li>
              ))}
            </ul>
          )}
          {consultantRank.none > 0 && <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">{consultantRank.none.toLocaleString("en-IN")} visits have no consultant.</p>}
        </section>
      </div>

    </div>
  );
}
